import { csvEscape } from "../../company/utils/csvAndDateUtils";
import type { UserUsageEntry } from "./types";
import {
  downloadCsvFile,
  formatDurationMinutes,
  resolveUserDisplayName,
} from "./utils";

interface ExportColumn {
  header: string;
  accessor: (entry: UserUsageEntry) => string;
}

/**
 * Export aggregated per-user usage data to CSV and trigger a download.
 *
 * @param data - The aggregated user rows to export (respects active filters)
 * @param columnHeaders - Column header labels matching the displayed table
 * @param filename - The name for the downloaded file
 * @param userDisplayNames - Map of user id to display name
 * @param userEmails - Map of user id to email address
 */
export function exportUserUsageCsv(
  data: UserUsageEntry[],
  columnHeaders: {
    user: string;
    email: string;
    sessions: string;
    uniqueExperiences: string;
    duration: string;
  },
  filename: string,
  userDisplayNames?: Map<string, string>,
  userEmails?: Map<string, string>,
): void {
  const columns: ExportColumn[] = [
    {
      header: columnHeaders.user,
      accessor: (e) => resolveUserDisplayName(e.userId, userDisplayNames),
    },
    {
      header: columnHeaders.email,
      accessor: (e) => userEmails?.get(e.userId) ?? "",
    },
    { header: columnHeaders.sessions, accessor: (e) => String(e.sessionCount) },
    {
      header: columnHeaders.uniqueExperiences,
      accessor: (e) => String(e.uniqueExperienceCount),
    },
    {
      header: columnHeaders.duration,
      accessor: (e) => formatDurationMinutes(e.totalDurationSeconds),
    },
  ];

  const headerRow = columns.map((col) => csvEscape(col.header)).join(",");
  const rows = data.map((entry) =>
    columns.map((col) => csvEscape(col.accessor(entry))).join(","),
  );

  const csvContent = [headerRow, ...rows].join("\r\n");
  downloadCsvFile(csvContent, filename);
}

/** Generate a default filename for the user usage export */
export function makeUserUsageExportFilename(
  startDate: Date,
  endDate: Date,
): string {
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  return `user-usage_${fmt(startDate)}_${fmt(endDate)}.csv`;
}
